import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useServerTable } from '@/components/data-grid';
import { http } from '@/api/http';
import { selectData } from '@/api/envelope';
import type { SuccessResponseDTO } from '@/api/envelope';
import {
  searchPosts,
  useGetPostDetail,
  useUpdatePostStatus as useUpdatePostStatusGenerated,
  getGetPostDetailQueryKey,
  updatePostStatus,
} from '@/api/generated/post-manager/post-manager';
import { useGetUserDetail } from '@/api/generated/user-manager/user-manager';
import type { PostManagerResponseDTO, PostRequestDTOPostTypesItem } from '@/api/generated/model';
import {
  isDeepScanSettled,
  isProvisionalNoAction,
  isTerminalCaseStatus,
} from '@/features/moderation/lib/verdictDisplay';
import { isAwaitingCaseDelivery } from '@/features/moderation/lib/coopDelivery';
import type { ModerationIntelligence } from '@/features/moderation/types';
import type { ModerationOverrideInput } from '@/features/moderation/api';

export const contentIntelligenceQueryKey = (contentId: number) =>
  ['content', 'intelligence', contentId] as const;

// Thin wrappers over the generated orval fetchers/hooks (doc 08 §4.3). Row-mapping is NOT done
// here — the DataGrid columns read raw DTO rows via toPostRow.

const CONTENT_LIST_KEY = ['content', 'search'] as const;
const PIPELINE_TEST_LIST_KEY = ['content', 'pipeline-test', 'search'] as const;

/** ~5 minutes at 3s — stop polling empty intelligence so we don't spin forever. */
const MISSING_CASE_MAX_POLLS = 100;

/** Server-paginated content list. Search hits `searchPosts`; pageSize=10 (CMP parity). */
export function useContentList() {
  return useServerTable<PostManagerResponseDTO>({
    queryKey: CONTENT_LIST_KEY,
    fetchPage: async ({ pageParam, pageSize, search, filters, sort }) => {
      // Server-side type filter: the `type` column's selected codes map to PostRequestDTO.postTypes.
      const postTypes = (filters?.type ?? []) as PostRequestDTOPostTypesItem[];
      // Server-side sorts — both accept 'asc'|'desc'. Only one column sorts at a time, so at most
      // one of these is set (TM-3323 added the release-time direction).
      const earningSortOrder = sort?.columnKey === 'earnings' ? sort.dir : undefined;
      const timeSortOrder = sort?.columnKey === 'time' ? sort.dir : undefined;
      // Real SearchPostsParams wraps the query in `requestDTO: PostRequestDTO`
      // (pageNumber / pageSize / search / postTypes / earningSortOrder).
      // Omit pipelineHarness — admin-api excludes harness posts by default.
      const res = await searchPosts({
        requestDTO: {
          pageNumber: pageParam,
          pageSize,
          ...(search ? { search } : {}),
          ...(postTypes.length ? { postTypes } : {}),
          ...(earningSortOrder ? { earningSortOrder } : {}),
          ...(timeSortOrder ? { timeSortOrder } : {}),
        },
      });
      const page = selectData(res);
      // Guard: enveloped `data` may be absent -> return an empty page (shared contract).
      if (!page) return { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
      return page;
    },
    pageSize: 10,
    mode: 'paged',
    urlKey: 'content', // keep search/filters/sort/page across list -> detail -> Back
  });
}

/** Server-paginated Pipeline Test harness list (pipelineHarness=true). */
export function usePipelineTestList() {
  return useServerTable<PostManagerResponseDTO>({
    queryKey: PIPELINE_TEST_LIST_KEY,
    fetchPage: async ({ pageParam, pageSize, search, filters, sort }) => {
      const postTypes = (filters?.type ?? []) as PostRequestDTOPostTypesItem[];
      const earningSortOrder = sort?.columnKey === 'earnings' ? sort.dir : undefined;
      const timeSortOrder = sort?.columnKey === 'time' ? sort.dir : undefined;
      const res = await searchPosts({
        requestDTO: {
          pageNumber: pageParam,
          pageSize,
          pipelineHarness: true,
          ...(search ? { search } : {}),
          ...(postTypes.length ? { postTypes } : {}),
          ...(earningSortOrder ? { earningSortOrder } : {}),
          ...(timeSortOrder ? { timeSortOrder } : {}),
        },
      });
      const page = selectData(res);
      if (!page) return { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
      return page;
    },
    pageSize: 10,
    mode: 'paged',
    urlKey: 'pipeline-test',
  });
}

/**
 * Detail load sequence (doc 08 §2.2): post detail -> creator (tolerated failure). The creator
 * query is disabled until we know the id, so a null creator degrades the card without breaking
 * the page. Non-numeric contentId is guarded to avoid a NaN request.
 */
export function useContentDetail(contentId: string) {
  const id = Number(contentId);
  const validId = Number.isFinite(id) && id > 0;

  const detail = useGetPostDetail(id, {
    query: { enabled: validId, select: selectData },
  });

  // The admin detail DTO now carries the real author via `creatorId` + `creator*` fields (the old
  // code wrongly used `collaboratorId` as the author). Use `creatorId` to drive the cross-nav and
  // an enrichment lookup that adds the creator's userType / account status to the card.
  const rawCreatorId = detail.data?.creatorId;
  const creatorId = rawCreatorId != null && rawCreatorId > 0 ? rawCreatorId : null;

  const creator = useGetUserDetail(creatorId ?? 0, {
    query: { enabled: creatorId != null, select: selectData },
  });

  return { detail, creator, creatorId, validId };
}

/**
 * Coop intelligence for a content id (media-manager case `tipmi:post:{id}`).
 * Shared by overview strip, description, and Coop cards so they share one query cache.
 *
 * @param pollWhileMissing When TipMi ledger is still PENDING and intelligence is null,
 *   keep polling briefly so a late outbox delivery appears without a manual refresh.
 */
export function useContentIntelligence(
  contentId: string,
  options?: { pollWhileMissing?: boolean },
) {
  const id = Number(contentId);
  const valid = Number.isFinite(id) && id > 0;
  const pollWhileMissing = options?.pollWhileMissing === true;

  return useQuery({
    queryKey: contentIntelligenceQueryKey(id),
    queryFn: async () => {
      const res = await http.get<SuccessResponseDTO<ModerationIntelligence>>(
        `/admin/api/moderation/content/${id}/intelligence`,
      );
      return selectData<ModerationIntelligence>(res.data);
    },
    enabled: valid,
    retry: false,
    // Intelligence may not exist for all content — graceful null.
    staleTime: 60_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data) {
        if (isAwaitingCaseDelivery(data.delivery_status, data.case_status)) return 3000;
        const deepScanSettled = isDeepScanSettled({
          deepScanDurationMs: data.deep_scan_duration_ms,
          deliveryStatus: data.delivery_status,
          vectors: data.vectors,
        });
        if (isProvisionalNoAction(data.verdict, data.case_status, { deepScanSettled })) return 3000;
        if (data.case_status && !isTerminalCaseStatus(data.case_status)) return 3000;
        return false;
      }
      // Null / missing mirror: keep polling only while TipMi ledger still looks in-flight.
      if (!pollWhileMissing) return false;
      if (query.state.dataUpdateCount >= MISSING_CASE_MAX_POLLS) return false;
      return 3000;
    },
  });
}

/**
 * Take Down / List Content. Mirrors CMP's "refetch after mutate" behavior by invalidating the
 * post detail (source of truth) plus the content list so the row badge stays consistent.
 */
export function useUpdatePostStatus(contentId: string) {
  const qc = useQueryClient();
  return useUpdatePostStatusGenerated({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: getGetPostDetailQueryKey(Number(contentId)) });
        void qc.invalidateQueries({ queryKey: CONTENT_LIST_KEY });
        void qc.invalidateQueries({ queryKey: PIPELINE_TEST_LIST_KEY });
      },
    },
  });
}

async function postContentModerationOverride(
  contentId: number,
  data: ModerationOverrideInput,
): Promise<Record<string, unknown>> {
  const res = await http.post<SuccessResponseDTO<Record<string, unknown>>>(
    `/admin/api/moderation/content/${contentId}/override`,
    data,
  );
  return selectData(res.data);
}

function invalidateContentModerationQueries(qc: QueryClient, contentId?: number) {
  if (contentId != null) {
    void qc.invalidateQueries({ queryKey: getGetPostDetailQueryKey(contentId) });
    void qc.invalidateQueries({ queryKey: contentIntelligenceQueryKey(contentId) });
  }
  void qc.invalidateQueries({ queryKey: CONTENT_LIST_KEY });
  void qc.invalidateQueries({ queryKey: ['moderation', 'coop', 'cases'] });
  void qc.invalidateQueries({ queryKey: ['moderation', 'coop', 'metrics'] });
}

/** POST /admin/api/moderation/content/{id}/override — Human Review Approve / Reject. */
export function useContentModerationOverride(contentId: string) {
  const qc = useQueryClient();
  const id = Number(contentId);
  return useMutation({
    mutationFn: async (data: ModerationOverrideInput) => postContentModerationOverride(id, data),
    onSuccess: () => invalidateContentModerationQueries(qc, id),
  });
}

export interface BulkContentModerationResult {
  succeeded: number;
  failed: number;
  total: number;
}

/**
 * Bulk approve/reject via the existing per-content override endpoint.
 * Settles each call independently so one failure does not abort the rest.
 */
export function useBulkContentModerationOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contentIds,
      data,
    }: {
      contentIds: number[];
      data: ModerationOverrideInput;
    }): Promise<BulkContentModerationResult> => {
      const uniqueIds = [...new Set(contentIds.filter((id) => Number.isFinite(id) && id > 0))];
      const results = await Promise.allSettled(
        uniqueIds.map((id) => postContentModerationOverride(id, data)),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      return { succeeded, failed: results.length - succeeded, total: results.length };
    },
    onSuccess: () => invalidateContentModerationQueries(qc),
  });
}

function invalidateUserTopContentQueries(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (q) => {
      if (!Array.isArray(q.queryKey) || q.queryKey.length === 0) return false;
      // All Content grid (useUserTopContent / useServerTable).
      if (q.queryKey[0] === 'users' && q.queryKey[2] === 'top-content') return true;
      // Engagement Top Content preview + Total Contents (useGetTopContent / orval).
      const head = q.queryKey[0];
      return typeof head === 'string' && head.includes('/statistics/top-content');
    },
  });
}

/**
 * Bulk Take Down / permanent Delete for user All Content (and similar grids).
 * Take Down = FORCE_REJECT (same path as content detail Take Down).
 * Delete = FORCE_REJECT then TipMi TER (same path as content detail Permanently Delete).
 */
export function useBulkContentTakeDownOrDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contentIds,
      mode,
      reason,
    }: {
      contentIds: number[];
      mode: 'takeDown' | 'delete';
      reason?: string;
    }): Promise<BulkContentModerationResult> => {
      const uniqueIds = [...new Set(contentIds.filter((id) => Number.isFinite(id) && id > 0))];
      const reasonText =
        reason?.trim() || (mode === 'delete' ? 'Admin terminate' : 'Admin take down');
      const results = await Promise.allSettled(
        uniqueIds.map(async (id) => {
          await postContentModerationOverride(id, {
            action: 'FORCE_REJECT',
            reason: reasonText,
          });
          if (mode === 'delete') {
            await updatePostStatus(id, { postStatus: 'TER', reason: reasonText });
          }
        }),
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      return { succeeded, failed: results.length - succeeded, total: results.length };
    },
    onSuccess: () => {
      invalidateContentModerationQueries(qc);
      invalidateUserTopContentQueries(qc);
    },
  });
}
